import type { SessionState } from "@/data/identity/session-store";
import type { Session } from "@/data/identity/types";
import { needsOrganisationSetup } from "@/data/identity/logic/invitations";
import { safeReturnTo } from "@/data/identity/logic/return-to";
import type { OnboardingStep, Route } from "@/router/routes";

/**
 * Who sees which page. Public pages render without the app frame and need no session; app pages need a session
 * that has finished first run; first-run steps run in order. Pure, so every rule is tested in gate.test.ts.
 */

export type GateDecision =
  /** The session is still loading. */
  | { kind: "wait" }
  /** Replace the URL with `to`. */
  | { kind: "redirect"; to: string }
  /** Render the page alone: no session needed and no app chrome (FR-PROJ-063 for share links). */
  | { kind: "public" }
  /** A signed-in page. `chrome` mounts the palette and banners; the session-expired dialog shows over any of them. */
  | { kind: "app"; chrome: boolean };

/** First-run order (FR-ADMIN-001 terms and 18+, FR-ADMIN-009 organisation setup, FR-PROJ-087 workspace choice). */
export const FIRST_RUN_ORDER: readonly OnboardingStep[] = ["terms", "organisation", "workspace"];

export const NEXT_PARAM = "next";

const PUBLIC_ROUTES: ReadonlySet<Route["name"]> = new Set([
  "signIn",
  "signUp",
  "authCallback",
  "requestAccess",
  "invite",
  "share",
  "staff",
  "notFound",
]);

export function pendingSteps(session: Session): OnboardingStep[] {
  const organisation = session.organisation;
  const pending: Record<string, boolean> = {
    terms: session.terms.accepted !== session.terms.current,
    organisation:
      organisation !== undefined &&
      needsOrganisationSetup(organisation.role, organisation.setupComplete),
    workspace: !session.workspaceChosen,
  };
  return FIRST_RUN_ORDER.filter((step) => pending[step] === true);
}

function withNext(path: string, next: string): string {
  return `${path}?${NEXT_PARAM}=${encodeURIComponent(next)}`;
}

export interface GateLocation {
  /** Path, query and hash of the current URL. */
  href: string;
  /** The `next` query parameter, if any. */
  next: string | null;
}

export function decideGate(
  route: Route,
  location: GateLocation,
  state: SessionState,
): GateDecision {
  if (PUBLIC_ROUTES.has(route.name)) return { kind: "public" };
  if (state.status === "loading") return { kind: "wait" };

  if (route.name === "landing") {
    return state.status === "active"
      ? { kind: "redirect", to: safeReturnTo(location.next) }
      : { kind: "public" };
  }

  // Every other route is an app page and needs a session.
  if (state.status === "signedOut")
    return { kind: "redirect", to: withNext("/welcome", location.href) };
  // Ended while open: keep the page and its state; the dialog asks to sign in again.
  if (state.status === "expired") return { kind: "app", chrome: route.name !== "onboarding" };

  const session = state.session;
  if (session.organisation === undefined) return { kind: "redirect", to: "/request-access" };

  // No plan, nothing works (D-034): the only page is the explanation, and it has no app chrome.
  if (!session.organisation.activated)
    return route.name === "notActivated"
      ? { kind: "app", chrome: false }
      : { kind: "redirect", to: "/not-activated" };
  if (route.name === "notActivated") return { kind: "redirect", to: "/" };

  const pending = pendingSteps(session);
  if (route.name === "onboarding") {
    const first = pending[0];
    if (first === undefined) return { kind: "redirect", to: safeReturnTo(location.next) };
    if (route.step !== first) {
      return {
        kind: "redirect",
        to:
          location.next === null
            ? `/onboarding/${first}`
            : withNext(`/onboarding/${first}`, location.next),
      };
    }
    return { kind: "app", chrome: false };
  }
  const first = pending[0];
  if (first !== undefined)
    return { kind: "redirect", to: withNext(`/onboarding/${first}`, location.href) };
  return { kind: "app", chrome: true };
}
